import { useId, useLayoutEffect, useState } from 'react';
import { dialogAt, popOverlay, pushOverlay } from './layers';

/**
 * The z-index rung for one overlay, based on how many are open beneath it
 * (operator-parity phase 21).
 *
 * This exists because dialog-over-dialog cannot be solved by portaling. In
 * every real case here the second overlay is a SIBLING — `CbamPortalSettingsModal`
 * renders after `</CgDialog>`, `SimulationDrawer`'s confirm after `</CgDrawer>` —
 * and siblings share no stacking context, so only a number can order them.
 * Before this hook six call sites hand-wrote `zIndex={1200}` / `{10010}`.
 *
 * Keyed on `open`, never on mount: a mounted-but-closed dialog holds no rung,
 * so a page that renders three dialogs and opens one gives it depth 0. Release
 * is in the effect's cleanup, so a parent closing under an open child cannot
 * strand a rung.
 *
 * @param {boolean} open Whether this overlay is currently shown.
 * @returns {{ overlay: number, content: number, depth: number }}
 */
export const useOverlayDepth = (open) => {
  const token = useId();
  const [depth, setDepth] = useState(-1);

  useLayoutEffect(() => {
    if (!open) {
      popOverlay(token);
      setDepth(-1);
      return undefined;
    }
    setDepth(pushOverlay(token));
    return () => popOverlay(token);
  }, [open, token]);

  // A closed overlay reports the base rung so its style object stays stable;
  // nothing paints at that moment anyway.
  return { ...dialogAt(depth < 0 ? 0 : depth), depth };
};

export default useOverlayDepth;
